/**
 * <s-rail> — a horizontal, scroll-snapping row of cards with previous/next buttons.
 * Markup: <s-rail id="…"> … <div data-rail-track> cards </div> … </s-rail>
 * Buttons: <button commandfor="{id}" command="--prev|--next">. Buttons disable at either end.
 */
class SRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]');
    if (!this.track) return;
    this.onCommand = (event) => {
      if (event.command !== '--prev' && event.command !== '--next') return;
      const direction = event.command === '--next' ? 1 : -1;
      const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const distance = direction * rtl * this.track.clientWidth * 0.8;
      if (reduce) {
        this.track.scrollBy({ left: distance, behavior: 'auto' });
        return;
      }
      this.glide(distance);
    };
    this.addEventListener('command', this.onCommand);
    this.onScroll = () => this.updateButtons();
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    // Edge-to-edge rails: the track's inline padding (in px) becomes its snap padding, so the first card rests
    // in line with the section heading on load and cards only slide past the screen edge once the shopper scrolls.
    this.alignStart = () => {
      const pad = getComputedStyle(this.track).paddingInlineStart;
      this.track.style.scrollPaddingInline = pad;
    };
    this.alignStart();
    this.track.scrollLeft = 0;
    // Re-check the arrows whenever the row changes size: a row inside a closed drawer measures 0 wide at first,
    // which would leave both arrows disabled once the drawer opens.
    this.resizeObserver = new ResizeObserver(() => {
      this.alignStart();
      this.updateButtons();
    });
    this.resizeObserver.observe(this.track);
    if (this.track.firstElementChild) this.resizeObserver.observe(this.track.firstElementChild);
    this.updateButtons();
  }

  disconnectedCallback() {
    this.removeEventListener('command', this.onCommand);
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.glideFrame);
  }

  // Slow, eased glide for the prev/next buttons (about 1.1s). Snapping is paused while it runs so the browser
  // does not jump to the next card, then restored so swiping still settles on a card.
  glide(distance) {
    const track = this.track;
    cancelAnimationFrame(this.glideFrame);
    const max = track.scrollWidth - track.clientWidth;
    const start = track.scrollLeft;
    const lowest = Math.min(0, -max);
    const highest = Math.max(0, max);
    let target = Math.min(highest, Math.max(lowest, start + distance));
    // Land exactly on a card's resting point so restoring snap does not nudge the row afterwards.
    if (getComputedStyle(track).direction !== 'rtl') {
      const pad = parseFloat(getComputedStyle(track).paddingInlineStart) || 0;
      const trackLeft = track.getBoundingClientRect().left;
      const stops = [...track.children].map((card) => Math.min(max, Math.max(0, card.getBoundingClientRect().left - trackLeft + start - pad)));
      if (stops.length) target = stops.reduce((best, stop) => (Math.abs(stop - target) < Math.abs(best - target) ? stop : best));
    }
    const change = target - start;
    if (Math.abs(change) < 1) return;
    const duration = 1100;
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    track.style.scrollSnapType = 'none';
    track.style.scrollBehavior = 'auto';
    let begin = null;
    const step = (now) => {
      if (begin === null) begin = now;
      const progress = Math.min(1, (now - begin) / duration);
      track.scrollLeft = start + change * ease(progress);
      if (progress < 1) {
        this.glideFrame = requestAnimationFrame(step);
      } else {
        track.style.scrollSnapType = '';
        track.style.scrollBehavior = '';
      }
    };
    this.glideFrame = requestAnimationFrame(step);
  }

  updateButtons() {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const position = Math.abs(this.track.scrollLeft);
    document.querySelectorAll(`[commandfor="${this.id}"]`).forEach((button) => {
      if (button.getAttribute('command') === '--prev') button.disabled = position <= 2;
      if (button.getAttribute('command') === '--next') button.disabled = position >= max - 2;
    });
  }
}

if (!customElements.get('s-rail')) customElements.define('s-rail', SRail);
